/**
 * MailChimp Service
 * Handles all MailChimp integration related operations
 */

import { apiRequest } from '../utils/apiResponseHandler';
import { getApiUrl } from '../config/api';

const mailchimpService = {
  // ==================== MAILCHIMP LISTS MANAGEMENT ====================

  // List MailChimp Lists
  async getMailChimpLists(filters = {}) {
    const queryParams = new URLSearchParams();
    Object.keys(filters).forEach(key => {
      if (filters[key] !== undefined && filters[key] !== '') {
        queryParams.append(key, filters[key]);
      }
    });
    
    const url = queryParams.toString() 
      ? `${getApiUrl('MAILCHIMP_LISTS')}?${queryParams.toString()}`
      : getApiUrl('MAILCHIMP_LISTS');
    
    return await apiRequest(url, {
      method: 'GET',
      showSuccessMessage: false,
      showErrorMessage: true
    });
  },

  // Get MailChimp List by ID
  async getMailChimpListById(listId) {
    return await apiRequest(getApiUrl('MAILCHIMP_LIST_BY_ID', listId), {
      method: 'GET',
      showSuccessMessage: false,
      showErrorMessage: true
    });
  },

  // Create MailChimp List
  async createMailChimpList(listData) {
    return await apiRequest(getApiUrl('MAILCHIMP_LISTS'), {
      method: 'POST',
      body: listData,
      showSuccessMessage: true,
      showErrorMessage: true
    });
  },

  // Update MailChimp List
  async updateMailChimpList(listId, listData) {
    return await apiRequest(getApiUrl('MAILCHIMP_LIST_BY_ID', listId), {
      method: 'PUT',
      body: listData,
      showSuccessMessage: true,
      showErrorMessage: true
    });
  },

  // Delete MailChimp List
  async deleteMailChimpList(listId) {
    return await apiRequest(getApiUrl('MAILCHIMP_LIST_BY_ID', listId), {
      method: 'DELETE',
      showSuccessMessage: true,
      showErrorMessage: true
    });
  },


  // Set Default List
  async setDefaultList(listId) {
    return await apiRequest(getApiUrl('MAILCHIMP_SET_DEFAULT', listId), {
      method: 'POST',
      showSuccessMessage: true,
      showErrorMessage: true
    });
  },

  // Bulk Sync Members
  async bulkSyncMembers() {
    return await apiRequest(getApiUrl('MAILCHIMP_BULK_SYNC_MEMBERS'), {
      method: 'POST',
      showSuccessMessage: true,
      showErrorMessage: true
    });
  },

  // ==================== MAILCHIMP SYNC MANAGEMENT ====================

  // List MailChimp Syncs
  async getMailChimpSyncs(filters = {}) {
    const queryParams = new URLSearchParams();
    Object.keys(filters).forEach(key => {
      if (filters[key] !== undefined && filters[key] !== '') {
        queryParams.append(key, filters[key]);
      }
    });
    
    const url = queryParams.toString() 
      ? `${getApiUrl('MAILCHIMP_SYNCS')}?${queryParams.toString()}`
      : getApiUrl('MAILCHIMP_SYNCS');
    
    return await apiRequest(url, {
      method: 'GET',
      showSuccessMessage: false,
      showErrorMessage: true
    });
  },

  // Get MailChimp Sync by ID
  async getMailChimpSyncById(syncId) {
    return await apiRequest(getApiUrl('MAILCHIMP_SYNC_BY_ID', syncId), {
      method: 'GET',
      showSuccessMessage: false,
      showErrorMessage: true
    });
  },

  // Sync Member
  async syncMember(memberId, memberData = {}) {
    return await apiRequest(getApiUrl('MAILCHIMP_MANUAL_SYNC', memberId), {
      method: 'POST',
      body: memberData,
      showSuccessMessage: true,
      showErrorMessage: true
    });
  },


  // ==================== MAILCHIMP INTEGRATION ====================

  // Get MailChimp Dashboard
  async getMailChimpDashboard() {
    return await apiRequest(getApiUrl('MAILCHIMP_DASHBOARD'), {
      method: 'GET',
      showSuccessMessage: false,
      showErrorMessage: true
    });
  },

  // Get Campaigns
  async getCampaigns(filters = {}) {
    const queryParams = new URLSearchParams();
    Object.keys(filters).forEach(key => {
      if (filters[key] !== undefined && filters[key] !== '') {
        queryParams.append(key, filters[key]);
      }
    });
    
    const url = queryParams.toString() 
      ? `${getApiUrl('MAILCHIMP_CAMPAIGNS')}?${queryParams.toString()}`
      : getApiUrl('MAILCHIMP_CAMPAIGNS');
    
    return await apiRequest(url, {
      method: 'GET',
      showSuccessMessage: false,
      showErrorMessage: true
    });
  },

  // Get Campaign by ID
  async getCampaignById(campaignId) {
    return await apiRequest(getApiUrl('MAILCHIMP_CAMPAIGN_BY_ID', campaignId), {
      method: 'GET',
      showSuccessMessage: false,
      showErrorMessage: true
    });
  },

  // Get Campaign Reports
  async getCampaignReports(campaignId) {
    return await apiRequest(getApiUrl('MAILCHIMP_CAMPAIGN_REPORTS', campaignId), {
      method: 'GET',
      showSuccessMessage: false,
      showErrorMessage: true
    });
  },

  // Send Campaign
  async sendCampaign(campaignId) {
    return await apiRequest(getApiUrl('MAILCHIMP_CAMPAIGN_SEND', campaignId), {
      method: 'POST',
      showSuccessMessage: true,
      showErrorMessage: true
    });
  },

  // Get List Statistics
  async getListStats(listId) {
    return await apiRequest(getApiUrl('MAILCHIMP_LIST_STATS', listId), {
      method: 'GET',
      showSuccessMessage: false,
      showErrorMessage: true
    });
  },

  // Get List Members
  async getListMembers(listId, filters = {}) {
    const queryParams = new URLSearchParams();
    Object.keys(filters).forEach(key => {
      if (filters[key] !== undefined && filters[key] !== '') {
        queryParams.append(key, filters[key]);
      }
    });
    
    const url = queryParams.toString() 
      ? `${getApiUrl('MAILCHIMP_LIST_MEMBERS', listId)}?${queryParams.toString()}`
      : getApiUrl('MAILCHIMP_LIST_MEMBERS', listId);
    
    return await apiRequest(url, {
      method: 'GET',
      showSuccessMessage: false,
      showErrorMessage: true
    });
  },

  // Bulk Sync to MailChimp
  async bulkSyncToMailChimp(syncData) {
    return await apiRequest(getApiUrl('MAILCHIMP_BULK_SYNC'), {
      method: 'POST',
      body: syncData,
      showSuccessMessage: true,
      showErrorMessage: true
    });
  },

  // Sync All Members
  async syncAllMembers() {
    return await apiRequest(getApiUrl('MAILCHIMP_SYNC_ALL'), {
      method: 'POST',
      showSuccessMessage: true,
      showErrorMessage: true
    });
  },

  // Test MailChimp Connection
  async testConnection() {
    try {
      const response = await apiRequest(getApiUrl('MAILCHIMP_TEST_CONNECTION'), {
        method: 'POST',
        showSuccessMessage: false, // We'll handle messages in the component
        showErrorMessage: false
      });
      
      // Ensure consistent response format
      return {
        success: response.success || false,
        message: response.message || (response.success ? 'Connection successful' : 'Connection failed'),
        data: response.data || null
      };
    } catch (error) {
      return {
        success: false,
        message: error.message || 'Connection test failed',
        data: null
      };
    }
  },

  // Get Account Info
  async getAccountInfo() {
    return await apiRequest(getApiUrl('MAILCHIMP_ACCOUNT_INFO'), {
      method: 'GET',
      showSuccessMessage: false,
      showErrorMessage: true
    });
  },

  // Update Email Preferences
  async updateEmailPreferences(memberId, preferences) {
    return await apiRequest(getApiUrl('MAILCHIMP_EMAIL_PREFERENCES', memberId), {
      method: 'PUT',
      body: preferences,
      showSuccessMessage: true,
      showErrorMessage: true
    });
  },

  // Manual Member Sync
  async manualMemberSync(memberId) {
    return await apiRequest(getApiUrl('MAILCHIMP_MANUAL_SYNC', memberId), {
      method: 'POST',
      showSuccessMessage: true,
      showErrorMessage: true
    });
  },

  // ==================== UTILITY FUNCTIONS ====================

  // Validate Email Preferences
  validateEmailPreferences(preferences) {
    const errors = {};
    
    if (typeof preferences.email_subscribed !== 'boolean') {
      errors.email_subscribed = 'Email subscription status is required';
    }
    
    if (preferences.email_preferences) {
      const validPreferences = ['newsletter', 'event_notifications', 'invoice_notifications', 'member_updates', 'admin_communications'];
      Object.keys(preferences.email_preferences).forEach(key => {
        if (!validPreferences.includes(key)) {
          errors[`email_preferences.${key}`] = 'Invalid preference key';
        }
        if (typeof preferences.email_preferences[key] !== 'boolean') {
          errors[`email_preferences.${key}`] = 'Preference value must be boolean';
        }
      });
    }
    
    return {
      isValid: Object.keys(errors).length === 0,
      errors
    };
  },

  // Format Email Preferences for API
  formatEmailPreferencesForAPI(preferences) {
    return {
      email_subscribed: preferences.email_subscribed === true,
      email_preferences: {
        newsletter: preferences.email_preferences?.newsletter === true,
        event_notifications: preferences.email_preferences?.event_notifications === true,
        invoice_notifications: preferences.email_preferences?.invoice_notifications === true,
        member_updates: preferences.email_preferences?.member_updates === true,
        admin_communications: preferences.email_preferences?.admin_communications === true
      }
    };
  },

  // Get Sync Status Badge
  getSyncStatusBadge(sync) {
    switch (sync.mailchimp_sync_status || sync.sync_status) {
      case 'synced':
        return { text: 'Synced', class: 'synced', color: '#38a169' };
      case 'pending':
        return { text: 'Pending', class: 'pending', color: '#d69e2e' };
      case 'failed':
        return { text: 'Failed', class: 'failed', color: '#e53e3e' };
      case 'unsubscribed':
        return { text: 'Unsubscribed', class: 'unsubscribed', color: '#718096' };
      case 'cleaned':
        return { text: 'Cleaned', class: 'cleaned', color: '#a0aec0' };
      default:
        return { text: 'Unknown', class: 'unknown', color: '#718096' };
    }
  },

  // Format Sync Data for Display
  formatSyncData(sync) {
    return {
      ...sync,
      status_badge: this.getSyncStatusBadge(sync),
      formatted_mailchimp_synced_at: sync.mailchimp_synced_at ? 
        new Date(sync.mailchimp_synced_at).toLocaleString() : 'Never',
      formatted_mailchimp_last_updated: sync.mailchimp_last_updated ? 
        new Date(sync.mailchimp_last_updated).toLocaleString() : 'Never'
    };
  },

  // Get List Status Badge
  getListStatusBadge(list) {
    if (!list.is_active) {
      return { text: 'Inactive', class: 'inactive', color: '#e53e3e' };
    }
    
    if (list.is_default) {
      return { text: 'Default', class: 'default', color: '#3182ce' };
    }
    
    switch (list.sync_status) {
      case 'synced':
        return { text: 'Synced', class: 'synced', color: '#38a169' };
      case 'never':
        return { text: 'Never Synced', class: 'never', color: '#d69e2e' };
      case 'failed':
        return { text: 'Sync Failed', class: 'failed', color: '#e53e3e' };
      default:
        return { text: 'Unknown', class: 'unknown', color: '#718096' };
    }
  },

  // Format List Data for Display
  formatListData(list) {
    return {
      ...list,
      status_badge: this.getListStatusBadge(list),
      formatted_last_synced_at: list.last_synced_at ? 
        new Date(list.last_synced_at).toLocaleString() : 'Never'
    };
  }
};

export { mailchimpService };
export default mailchimpService;
